import { useLayoutEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { useFormField } from '@witmetrics/data-store';
import { isBooleanField } from '@witmetrics/utils/forms';
import { scrollIntoView } from '@/utils/dom';
import { useFormProvider } from '@/providers/FormProvider';
import FieldTypeIcon from '@/components/FieldTypeIcon';
import Note from '@/components/Note';
import FieldInput from './FieldInput';
import { FIELDS } from './constants';
import { classes, getInputType } from './utils';

export type FieldBlockProps = {
  active?: boolean;
  fieldId: number;
};

export default function FieldBlock({ active, fieldId }: FieldBlockProps) {
  const activeRef = useRef(!!active);
  const listItemRef = useRef<HTMLLIElement>(null);
  const field = useFormField(fieldId);
  const { answers, onUpdateAnswer } = useFormProvider();

  useLayoutEffect(() => {
    if (active && !activeRef.current) scrollIntoView(listItemRef.current);
    activeRef.current = !!active;
  }, [active]);

  const handleChange = (value: string) => {
    onUpdateAnswer(fieldId, value);
  };

  const renderInput = (inputClassName?: string) => {
    return (
      <FieldInput
        className={inputClassName}
        fieldId={fieldId}
        value={answers[fieldId]}
        onChange={handleChange}
      />
    );
  };

  if (!field) {
    console.warn(`Field not found (id: ${fieldId})`);
    return null;
  }

  const fieldType = FIELDS[field.formFieldTypeId];
  const { isInline } = getInputType(field.formFieldTypeId);

  return (
    <li
      key={field.id}
      ref={listItemRef}
      className={twMerge(classes.wrapper, active && classes.active)}>
      <div className={classes.iconWrapper}>
        <FieldTypeIcon fieldTypeId={fieldType.id} />
      </div>
      <div
        className={twMerge(
          classes.contentWrapper,
          isInline ? classes.inlineContent : classes.wrappedContent
        )}>
        <div>
          <h2 className={classes.title}>{field.label}</h2>
          <Note
            className={classes.description}
            value={field.description || ''}
          />
        </div>
        {isBooleanField(field.formFieldTypeId) ? (
          <div className={classes.booleanWrapper}>{renderInput()}</div>
        ) : (
          renderInput(classes.input)
        )}
      </div>
    </li>
  );
}
